import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { Company, CompanySelection } from '../types';
import { companyAPI } from '../services/api';
import { setEncryptionEnabled, setCurrentCompanyId } from '../utils/EncryptionUtils';

interface CompanyContextType {
  selectedCompany: CompanySelection;
  companies: Company[];
  loading: boolean;
  error: string | null;
  setSelectedCompany: (companyId: string | null) => void;
  loadCompanies: () => Promise<void>;
  clearSelection: () => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [selectedCompany, setSelectedCompanyState] = useState<CompanySelection>({
    selectedCompanyId: null,
    selectedCompany: null
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load company summaries from API
  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyAPI.getAllCompanies();
      setCompanies(data);
      // Auto-select first company if none selected
      if (data.length > 0 && !selectedCompany.selectedCompanyId) {
        setSelectedCompany(data[0].id);
      }
    } catch (err) {
      setError('Failed to load companies');
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Set selected company - fetches full details including logo
  const setSelectedCompany = async (companyId: string | null) => {
    if (companyId) {
      try {
        // Fetch full company details (with logo)
        const fullCompany = await companyAPI.getCompanyById(companyId);
        const selection: CompanySelection = {
          selectedCompanyId: companyId,
          selectedCompany: fullCompany
        };
        setSelectedCompanyState(selection);
        localStorage.setItem('selectedCompanyId', companyId);
        // Update encryption state
        setCurrentCompanyId(companyId);
        setEncryptionEnabled(!!fullCompany.encryptionFeatureEnabled);
      } catch (err) {
        console.error('Failed to fetch full company details:', err);
        // Fallback: use summary from list
        const summary = companies.find(c => c.id === companyId);
        if (summary) {
          setSelectedCompanyState({
            selectedCompanyId: companyId,
            selectedCompany: summary as Company // Cast summary to Company (logo will be undefined)
          });
          localStorage.setItem('selectedCompanyId', companyId);
          setCurrentCompanyId(companyId);
          setEncryptionEnabled(!!summary.encryptionFeatureEnabled);
        }
      }
    } else {
      setSelectedCompanyState({ selectedCompanyId: null, selectedCompany: null });
      localStorage.removeItem('selectedCompanyId');
      setCurrentCompanyId(null);
      setEncryptionEnabled(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedCompany(null);
  };

  // Load initial selection from localStorage
  useEffect(() => {
    const savedCompanyId = localStorage.getItem('selectedCompanyId');
    if (savedCompanyId) {
      setSelectedCompanyState(prev => ({
        ...prev,
        selectedCompanyId: savedCompanyId
      }));
    }
  }, []);

  // When companies load and we have a saved selection, fetch full details
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && companies.length > 0 && !selectedCompany.selectedCompany) {
      setSelectedCompany(selectedCompany.selectedCompanyId);
    }
  }, [companies, selectedCompany.selectedCompanyId]);

  // Load companies when token changes
  useEffect(() => {
    if (token) {
      loadCompanies();
    } else {
      setCompanies([]);
      setSelectedCompanyState({ selectedCompanyId: null, selectedCompany: null });
    }
  }, [token]);

  const value: CompanyContextType = {
    selectedCompany,
    companies,
    loading,
    error,
    setSelectedCompany,
    loadCompanies,
    clearSelection
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
